// JOBE-0105: ByOwnerBlock — side-rail block on the /projects index.
// Aggregates the visible-page rows by `relations.assignedTo[0]` and
// renders the top-5 owners as click-to-filter rows.
//
// Spec §Decision 4: client-side aggregation over the loaded rows.
// "Top N" label sits in the block header so the user knows the
// aggregation is page-local.

import React, { useMemo } from 'react';
import { topNByKey } from '../aggregations.js';
import { FRIENDLY_TOKENS } from '../../library/chipVocabulary.js';

const TOP_N = 5;

function urnSlug(urn) {
  if (!urn || typeof urn !== 'string') return '';
  const idx = urn.lastIndexOf(':');
  return idx >= 0 ? urn.slice(idx + 1) : urn;
}

function ownerOf(row) {
  const relations = (row && (row.relations
    || (row.payload && row.payload.relations))) || {};
  const assigned = relations.assignedTo;
  if (Array.isArray(assigned) && assigned.length > 0) return assigned[0];
  return null;
}

export function ByOwnerBlock({ rows, onAppendChip, roster }) {
  const top = useMemo(() => topNByKey(rows || [], ownerOf, TOP_N), [rows]);

  const labelFor = (urn) => {
    if (!urn) return '—';
    if (roster && Array.isArray(roster.humans)) {
      const h = roster.humans.find((p) => p?.urn === urn);
      if (h) return h.displayName || h.name || urnSlug(urn);
    }
    if (roster && Array.isArray(roster.agents)) {
      const a = roster.agents.find((p) => p?.urn === urn);
      if (a) return a.displayName || a.name || urnSlug(urn);
    }
    return urnSlug(urn);
  };

  const onPick = (ownerUrn) => {
    if (!onAppendChip || !ownerUrn) return;
    onAppendChip({
      kind: 'predicate',
      friendlyToken: 'owner',
      wirePredicate: FRIENDLY_TOKENS.owner?.wirePredicate || 'assignedTo',
      values: [ownerUrn],
    });
  };

  return (
    <section
      className="projects-rail-block projects-rail-by-owner"
      data-testid="projects-rail-by-owner"
    >
      <header className="projects-rail-block-header">
        <h3 className="projects-rail-block-title">By owner</h3>
        <span className="projects-rail-block-sub muted">top {TOP_N}</span>
      </header>
      {top.length === 0 ? (
        <p className="muted projects-rail-empty" data-testid="projects-rail-by-owner-empty">
          (none yet)
        </p>
      ) : (
        <ul className="projects-rail-list">
          {top.map((entry) => (
            <li key={entry.key}>
              <button
                type="button"
                className="projects-rail-row"
                onClick={() => onPick(entry.key)}
                data-testid={`projects-rail-by-owner-${urnSlug(entry.key)}`}
              >
                <span className="projects-rail-row-label">{labelFor(entry.key)}</span>
                <span className="projects-rail-row-count">{entry.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
